import React, { useState } from 'react';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import Utils from 'files/Utils.js';
import { waterSensorPerUnit, maxTierQty } from 'files/SensorPricing.js';
import { applyOffer, offerPricing } from 'files/LimitedOffer.js';
import PriceGate from 'views/PriceGate.js';
import ShopPayInstallments from 'views/ShopPayInstallments.js';
import OfferTag from 'views/OfferTag.js';
import { useServiceability } from 'views/ServiceabilityProvider.js';

const ProductCard = ({ product, onAddToCart, onViewDetails }) => {

    const [hover, setHover] = useState(false);
    const { canBuyOnline, offer, volumePricing } = useServiceability();
    const pricing = offerPricing(product, offer);
    const volumeFrom = volumePricing && product.handle === 'water-sensor' && product.tiers
        ? applyOffer(waterSensorPerUnit(product.tiers, maxTierQty(product.tiers)), pricing.percent)
        : 0;

    // Phones: a quiet list row (photo · name + price · round add button) instead
    // of a tall stacked card. Sized so the name, price and volume line each fit
    // on one line at 390px, so every row is the same height. The struck price
    // (plus the offer banner) carries the discount; description and installments
    // are left to the product page.
    if(Utils.isMobile()) {
        return (
            <div
                onClick={onViewDetails}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: 12,
                    borderRadius: 18,
                    backgroundColor: Appearance.colors.white,
                    border: `1px solid ${Appearance.colors.border()}`,
                    cursor: 'pointer',
                }}>
                <div style={{
                    width: 64,
                    height: 64,
                    flexShrink: 0,
                    borderRadius: 12,
                    overflow: 'hidden',
                    backgroundColor: Appearance.colors.offWhite,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    {product.images && product.images.length > 0 ? (
                        <img src={product.images[0].src} alt={product.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <i className={product.icon || 'fas fa-box'} style={{ fontSize: 30, color: product.color || Appearance.colors.primary(), opacity: 0.7 }} />
                    )}
                </div>

                <div style={{ flexGrow: 1, minWidth: 0 }}>
                    <div style={{ ...Appearance.textStyles.subHeading({ fontSize: 15.5 }), marginBottom: 2 }}>
                        {product.title}
                    </div>
                    <PriceGate block={false} buttonStyle={{ padding: '7px 14px', fontSize: 13 }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                                <span style={Appearance.textStyles.price({ fontSize: 16 })}>
                                    {Utils.formatCurrency(pricing.price)}
                                </span>
                                {pricing.was && (
                                    <span style={{ ...Appearance.textStyles.caption({ fontSize: 12.5 }), textDecoration: 'line-through', color: Appearance.colors.lightGrey }}>
                                        {Utils.formatCurrency(pricing.was)}
                                    </span>
                                )}
                            </div>
                            {volumeFrom > 0 && (
                                <div style={{ ...Appearance.textStyles.caption({ fontSize: 12.5 }), marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {`Multi-packs from ${Utils.formatCurrency(volumeFrom)}/ea`}
                                </div>
                            )}
                        </div>
                    </PriceGate>
                </div>

                {canBuyOnline && (
                    <button
                        aria-label={`Add ${product.title} to cart`}
                        onClick={(e) => { e.stopPropagation(); if(onAddToCart) { onAddToCart(); } }}
                        style={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            borderRadius: 20,
                            border: 'none',
                            backgroundColor: Appearance.colors.primary(),
                            color: Appearance.colors.white,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}>
                        <i className={'fas fa-plus'} style={{ fontSize: 15 }} />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div
            onClick={onViewDetails}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                ...Appearance.styles.card(),
                boxShadow: hover ? Appearance.boxShadow.cardHover : Appearance.boxShadow.card,
                transform: hover ? 'translateY(-4px)' : 'translateY(0)',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: 360,
                cursor: 'pointer',
            }}>

            {/* Product Image */}
            <div
                style={{
                    width: '100%',
                    height: 280,
                    backgroundColor: Appearance.colors.offWhite,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    position: 'relative',
                }}>
                {/* Limited-time offer — on the image so the price row stays uncrowded */}
                <OfferTag compact percent={pricing.percent} style={{ position: 'absolute', top: 14, left: 14, zIndex: 1 }} />
                {product.images && product.images.length > 0 ? (
                    <img
                        src={product.images[0].src}
                        alt={product.title}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.5s ease',
                            transform: hover ? 'scale(1.05)' : 'scale(1)',
                        }} />
                ) : (
                    <i
                        className={product.icon || 'fas fa-box'}
                        style={{
                            fontSize: 64,
                            color: product.color || Appearance.colors.primary(),
                            opacity: 0.7,
                            transition: 'transform 0.5s ease',
                            transform: hover ? 'scale(1.1)' : 'scale(1)',
                        }} />
                )}
            </div>

            {/* Product Info */}
            <div style={{
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
            }}>
                <span style={{
                    ...Appearance.textStyles.label(),
                    marginBottom: 8,
                    color: product.color || Appearance.colors.primary(),
                }}>
                    {product.subtitle || product.productType || 'Safety Device'}
                </span>

                <span
                    style={{
                        ...Appearance.textStyles.subHeading(),
                        marginBottom: 8,
                    }}>
                    {product.title}
                </span>

                <span style={{
                    ...Appearance.textStyles.body({ fontSize: 14 }),
                    marginBottom: 14,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}>
                    {Utils.firstSentence(product.description, 110)}
                </span>

                {volumePricing && product.handle === 'water-sensor' && product.tiers && (
                    <div style={{ marginBottom: 14 }}>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 7,
                            backgroundColor: Utils.hexToRGBA(Appearance.colors.primary(), 0.08),
                            color: Appearance.colors.primary(),
                            padding: '6px 12px',
                            borderRadius: 999,
                            fontFamily: Appearance.fonts.body,
                            fontSize: 12.5,
                            fontWeight: 600,
                        }}>
                            <i className={'fas fa-tags'} style={{ fontSize: 11 }} />
                            {`Volume pricing · from ${Utils.formatCurrency(applyOffer(waterSensorPerUnit(product.tiers, maxTierQty(product.tiers)), pricing.percent))}/ea`}
                        </span>
                    </div>
                )}

                <div style={{ flexGrow: 1 }} />

                {/* Price → installments → CTA — gated by serviceability */}
                <PriceGate>
                    <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                            <span style={Appearance.textStyles.price()}>
                                {Utils.formatCurrency(pricing.price)}
                            </span>
                            {pricing.was && (
                                <span style={{
                                    ...Appearance.textStyles.caption(),
                                    textDecoration: 'line-through',
                                    color: Appearance.colors.lightGrey,
                                }}>
                                    {Utils.formatCurrency(pricing.was)}
                                </span>
                            )}
                        </div>
                        {/* Spacing lives on the button, not the installments line —
                            that line renders nothing under $50 (e.g. a $49 sensor). */}
                        <ShopPayInstallments price={pricing.price} />
                        <span onClick={(e) => e.stopPropagation()} style={{ display: 'block', marginTop: 16 }}>
                            <Button
                                label={'Add To Cart'}
                                type={'primary'}
                                onClick={onAddToCart}
                                style={{ width: '100%', padding: '12px 20px', fontSize: 14 }} />
                        </span>
                    </div>
                </PriceGate>
            </div>
        </div>
    );
};

export default ProductCard;
